import {IEndPoint, IO} from "/@lib@/commons/io/io.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IRegPointer, REG} from "/@lib@/commons/registry.js";
import {ISignboard, Signboard} from "/@lib@/commons/utils/signboard.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {EButtonUiContext, IAction} from "/@lib@/commons/actions.js";
import {BaseElement, BaseElementAsync, MsgLabel, OLabelInit} from "/@back@/commons/basis.js";
import {IFormJsonisable} from "/@lib@/commons/forms.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {LANG} from "/@lib@/commons/lang.js";

type dynString<C> = (ctx: C, area: Area<C>) => string;
type dynBoolean<C> = (ctx: C, area: Area<C>) => boolean;
type dynAny<C, T = any> = (ctx: C, area: Area<C>) => T;

/**
 * Un Area est une factory d'un widget graphique (view, input...).
 *
 * Chaque Area possède :
 *  - les méta-données du widget (id, label, desc, icon, group),
 *  - la visibilité et les permissions d'affichage du widget,
 *  - les librairies nécessaires à charger en amont de l'instanciation du widget,
 *  - la fonction pour construire le widget.
 */
export interface IArea<C = {}, E extends HTMLElement = HTMLElement> extends ISignboard<C> {

	/** Cette Area exige-t-elle une chargement asynchrone de librairies ? */
	needAsync(ctx: C): boolean

	/**
	 * Construit une zone graphique.
	 * @param ctx Données permettant de construire et initialiser l'area.
	 * @return Node créé
	 */
	buildBody(ctx: C, lastDatas?: JLastDatas): E

	/**
	 * Construit une zone graphique en s'assurant que les libs nécessaires sont chargées.
	 * @param ctx Données permettant de construire et initialiser l'area.
	 * @return Node créé
	 */
	loadBody(ctx: C, lastDatas?: JLastDatas): Promise<E>

	/**
	 * Si needAsync===true force le chargement des libs async sans construire de body.
	 * Une fois la promesse résolue, needAsync devrait être égal à false, sauf si l'impl ne permet
	 * pas ce préchargement.
	 */
	loadLibs(ctx: C): Promise<void>
}

export function isArea<C, E extends HTMLElement>(a: any): a is IArea<C, E> {return a && typeof a.needAsync === "function" && typeof a.buildBody === "function"}

export class Area<C = {}, E extends HTMLElement = HTMLElement> extends Signboard<C> implements IArea<C, E> {

	protected _body: (ctx: C, area: Area<C, E>, lastDatas?: JLastDatas) => E;

	needAsync(ctx: C): boolean {return false}

	buildBody(ctx: C, lastDatas?: JLastDatas): E {
		return this._body(ctx, this, lastDatas);
	}

	async loadBody(ctx: C, lastDatas?: JLastDatas): Promise<E> {
		return this.buildBody(ctx, lastDatas);
	}

	async loadLibs(ctx: C): Promise<void> {}

	setBodyBuilder(body: (ctx: C, area: Area<C>, lastDatas?: JLastDatas) => E): this {
		this._body = body;
		return this;
	}

	/** Surcharge d'une propriété / méthode quelconque. */
	override(name: keyof Area, val: any): this {
		this[name] = val;
		return this;
	}

}

export class AreaAsync<C = {}, E extends HTMLElement = HTMLElement> extends Area<C, E> {

	protected _libsLoadedIn: Window;
	protected _libs: IEndPoint[];
	protected _modules: any[];

	async loadBody(ctx: C, lastDatas?: JLastDatas): Promise<E> {
		if (this._libsLoadedIn !== window) await this.loadLibs(ctx);
		return this.buildBody(ctx, lastDatas);
	}

	needAsync(ctx: C): boolean {
		if (this._libsLoadedIn === window) return false;
		return this._libs?.length > 0;
	}

	/** Préchargement des libs nécessaires. */
	async loadLibs(ctx: C): Promise<void> {
		if (this._libsLoadedIn === window) return;
		if (this._libs?.length) this._modules = await IO.importAllJs(this._libs);
		this._libsLoadedIn = window;
	}

	/**
	 * Chargement asycnhrone des librairies nécessaires à la construction de cet area.
	 */
	requireLib(...libs: IEndPoint[]): this {
		if (libs) {
			if (!this._libs) this._libs = libs;
			else IO.appendUrl(this._libs, ...libs);
		}
		return this;
	}
}


/**
 * Pointeur d'une area avec son contexte.
 * Ces propriétés peuvent être définies sur le widget construit par l'area ou sur son container permettant sa construction.
 * Exemple : pour un onglet d'un c-tabs, la ref à l'area et son contexte peut-être porté par l'objet interne c-tab et/ou
 *        par la view constituant le contenu de l'onglet.
 */
export interface IAreaPointer<C = {}, E extends HTMLElement = HTMLElement> {
	area?: IArea<C, E>;
	areaContext?: C;
}

export function isAreaPointer(h: any): h is IAreaPointer {return h && ((h as IAreaPointer).area != null)}


/** Area dont la construction du widget se résume à l'instanciation d'un simple tag. */
export class SimpleTagArea<C, E extends HTMLElement> extends AreaAsync<C, E> {
	tagName: string;

	constructor(customTag: string, libs?: IEndPoint | IEndPoint[], id?: string) {
		super(id || customTag);
		this.tagName = customTag;
		if (libs) Array.isArray(libs) ? this.requireLib(...libs) : this.requireLib(libs);
	}

	buildBody(ctx: C, lastDatas?: JLastDatas): E {
		const elt = this.newElt(ctx, lastDatas);
		if (!this.isVisible(ctx)) elt.setAttribute('hidden', '');
		return elt;
	}

	protected newElt(ctx: C, lastDatas?: JLastDatas): E {
		return document.createElement(this.tagName) as E;
	}
}

/** Area pour la construction d'une custom element avec initialisation (setInit/getInit) */
export class CustomTagArea<C, E extends BaseElement, EI = C> extends SimpleTagArea<C, E> {
	_init?: any | dynAny<C, EI>;

	buildBody(ctx: C, lastDatas?: JLastDatas): E {
		const elt = super.buildBody(ctx, lastDatas);
		elt.initialize(this.getInit(ctx));
		return elt;
	}

	setInit(ctx: any | dynAny<C, EI>): this {
		this._init = ctx;
		return this;
	}

	getInit(ctx: C): EI {
		if (this._init) {
			if (typeof this._init === 'function') return this._init(ctx);
			else return this._init;
		}
		return ctx as any as EI;
	}

}

/** Area pour la construction d'une custom element avec initialisation asynchrone (setInit/getInit) */
export class CustomAsyncTagArea<C, E extends BaseElementAsync, EI = C> extends SimpleTagArea<C, E> {
	_init?: any | dynAny<C, EI>;

	needAsync(ctx: C): boolean {return true}

	async loadBody(ctx: C, lastDatas?: JLastDatas): Promise<E> {
		const body = await super.loadBody(ctx, lastDatas);
		body.initialize(this.getInit(ctx));
		await body.initializedAsync;
		return body;
	}

	setInit(ctx: any | dynAny<C, EI>): this {
		this._init = ctx;
		return this;
	}

	getInit(ctx: C): EI {
		if (this._init) {
			if (typeof this._init === 'function') return this._init(ctx);
			else return this._init;
		}
		return ctx as any as EI;
	}
}

/**
 * Surcharge d'une area.
 * L'area surchargée peut être initialisée par setOverridenSvc() via le mécanisme de surcharge
 * des svc des extPoints ou appelé explicitement.
 * La méthode getWrappedArea(ctx) peut aussi être surchargée.
 */
export class AreaOver<C, E extends HTMLElement> extends AreaAsync<C, E> implements REG.IOverridingSvc {

	protected _subArea: Area<C, E>;

	/**
	 * Doit renvoyer ActionWrapper.NULL si pas de wrappedAction.
	 */
	getSubArea(ctx: C): Area<C, E> {return this._subArea || AREAS.NULL}

	/** Méthode utilisée par Reg.getSvc() pour affecter le svc surchargé avant de retourner le svc surchargeant. */
	setOverridenSvc(svc: any) {
		this._subArea = svc;
		return this;
	}

	getLabel(ctx: C) {
		return this._label !== undefined ? super.getLabel(ctx) : this.getSubArea(ctx).getLabel(ctx);
	}

	getDescription(ctx: C) {
		return this._description !== undefined ? super.getDescription(ctx) : this.getSubArea(ctx).getDescription(ctx);
	}

	getGroup(ctx: C) {
		return this._group !== undefined ? super.getGroup(ctx) : this.getSubArea(ctx).getGroup(ctx);
	}

	isVisible(ctx: C) {
		if (this._visible !== undefined) return super.isVisible(ctx);
		if (!this.checkObjectRootVisiblePerm(ctx)) return false;
		return this.getSubArea(ctx).isVisible(ctx);
	}

	buildBody(ctx: C, lastDatas?: JLastDatas): E {
		if (this._body) return super.buildBody(ctx, lastDatas);
		return this.getSubArea(ctx).buildBody(ctx, lastDatas);
	}

	async loadLibs(ctx: C): Promise<void> {
		await Promise.all([super.loadLibs(ctx), this._subArea.loadLibs(ctx)]);
	}
}

/**
 * Wrapper d'une area obtenue dynamiquement via un svc d'un extPoints.
 * La méthode setWrappedSvc(svcCd) permet d'initialiser ce wrapper.
 */
export class AreaOverFromSvc<C, E extends HTMLElement> extends AreaOver<C, E> {
	protected _svcCd: string;

	getWrappedSvcCd() {return this._svcCd}

	setWrappedSvcCd(svcCd: string): this {
		this._svcCd = svcCd;
		return this;
	}

	getSubArea(ctx: C): Area<C, E> {
		return REG.getReg(ctx).getSvc<Area<C, E>>(this._svcCd) || AREAS.NULL;
	}
}

/**
 * Interface pour le contexte de construction des contrôle d'un formulaire
 */
export interface IControlLabelContext {
	/**
	 * Si true, enveloppe l'input du label.
	 * TODO permet-on le positionnement du label ?
	 */
	buildControlLabel?: boolean,
}

export interface IControlAreaContext extends IControlLabelContext {}

/**
 * Area du type MsgLabel
 */
export class MsgArea<C> extends Area<C, MsgLabel> {
	constructor(id?: string, public msgInit?: OLabelInit) {
		super(id);
	}

	/** Construction de l'inputArea par défaut. */
	buildBody(ctx: C): MsgLabel {
		const msg = <MsgLabel î={this.msgInit}/> as MsgLabel;
		msg.setAttribute('area-id', this.getId());
		this._iniMsgElement(msg, ctx);
		return msg;
	}

	/** Initialise les comportement MsgLabel du body */
	protected _iniMsgElement(element: HTMLElement, ctx: C) {
		if (!this.isVisible(ctx)) DOM.setHidden(element, true);
		if (!this.isEnabled(ctx)) element.setAttribute('disabled', '');
	}

}

/**
 * Area du type [label Element]
 */
export abstract class LabelArea<C extends IControlLabelContext = IControlLabelContext> extends Area<C, HTMLElement> {

	/** Construction de l'inputArea par défaut. */
	buildBody(ctx: C): HTMLElement {
		const control = this._buildControl(ctx, this.getId());
		const body = ctx.buildControlLabel ? this._buildControlLabel(control, ctx) : control;
		body.setAttribute('area-id', this.getId());
		this._initBodyElement(body, ctx);
		if (!this.isVisible(ctx)) body.hidden = true;
		return body;
	}

	/** Initialise les comportements standard du body */
	protected _initBodyElement(element: HTMLElement, ctx: C) {
		const title = this.getDescription(ctx);
		if (title) element.title = title;
	}

	/**
	 * Initialise les comportement standard d'un contrôle (visible, enabled, required, etc...).
	 * L'initialistion d'un custom doit se faire en surchargeant cette méthode pour bénéfier de l'affectation des attributs
	 */
	protected _initStdControl(element: HTMLElement, ctx: C) {
		if (!this.isEnabled(ctx)) element.setAttribute('disabled', '');
	}

	protected _buildControlLabel(control: HTMLElement, ctx: C, label = this.getLabel(ctx)): HTMLElement {
		return AREAS.buildControlLabel(control, this.getId() + '~' + Math.floor(Math.random() * 100000).toString(36), label);
	}

	abstract _buildControl(ctx: C, name: string): HTMLElement;
}

/**
 * Area async du type [label Element]
 */
export abstract class LabelAsyncArea<C extends IControlLabelContext = IControlLabelContext> extends AreaAsync<C, HTMLElement> {

	/** Construction de l'inputArea par défaut. */
	async loadBody(ctx: C, lastDatas?: JLastDatas): Promise<HTMLElement> {
		const control = await this._loadControl(ctx, this.getId(), lastDatas);
		const body = this._buildControlLabel ? this._buildControlLabel(control, ctx) : control;
		body.setAttribute('area-id', this.getId());
		this._initBodyElement(body, ctx);
		return body;
	}

	/** Construction de l'inputArea par défaut. */
	buildBody(ctx: C): HTMLElement {
		const control = this._buildControl(ctx, this.getId());
		const body = ctx.buildControlLabel ? this._buildControlLabel(control, ctx) : control;
		body.setAttribute('area-id', this.getId());
		this._initBodyElement(body, ctx);
		return body;
	}

	/** Initialise les comportement standard du body */
	protected _initBodyElement(element: HTMLElement, ctx: C) {
		if (!this.isVisible(ctx)) DOM.setHidden(element, true);
	}

	/**
	 * Initialise les comportement standard d'un contrôle (visible, enabled, required, etc...).
	 * L'initialistion d'un custom doit se faire en surchargeant cette méthode pour bénéfier de l'affectation des attributs
	 */
	protected _initStdControl(element: HTMLElement, ctx: C) {
		if (!this.isEnabled(ctx)) element.setAttribute('disabled', '');
	}

	protected _buildControlLabel(control: HTMLElement, ctx: C, label = this.getLabel(ctx), desc = this.getDescription(ctx)): HTMLDivElement {
		const elemId = this.getId() + '~' + Math.floor(Math.random() * 100000).toString(36);
		control.id = elemId;
		return <div class="ctrlLbl">
			<label class="lbl" for={elemId} title={desc}>{label}</label>
			{control}
		</div> as HTMLDivElement;
	}

	protected _buildControl?(ctx: C, name: string, lastDatas?: JLastDatas): HTMLElement;

	abstract _loadControl(ctx: C, name: string, lastDatas?: JLastDatas): Promise<HTMLElement>;

	needAsync(ctx: C): boolean {
		return !("_buildControl" in this) ? true : super.needAsync(ctx);
	}

}


/**
 * Area regroupant les comportements pour les contrôle d'un formulaire (input, select et textarea)
 */
export abstract class ControlAsyncArea<C extends IControlAreaContext = IControlAreaContext> extends LabelAsyncArea<C> {
	protected _required: boolean | dynBoolean<C>;
	protected _defaultValue: any | dynAny<C>;
	protected _readOnly: any | dynBoolean<C>;

	setRequired(required: boolean | dynBoolean<C>): this {
		this._required = required;
		return this;
	}

	isRequired(ctx: C) {
		if (typeof this._required === 'function' && this._required(ctx, this) === true) return true;
		if (this._required === true) return true;
		return false;
	}

	/**
	 * Valeur par défaut de l'inputArea
	 * @type object
	 */
	setDefaultValue(defaultValue: any | dynAny<C>) {
		this._defaultValue = defaultValue;
		return this;
	}

	getDefaultValue(ctx: C) {
		return (typeof this._defaultValue === 'function') ? this._defaultValue(ctx, this) : this._defaultValue;
	}

	/**
	 * Indique que le champ est protégé, et enverra un évènement
	 * @param protected
	 */
	setReadOnly(locked: boolean | dynBoolean<C>): this {
		this._readOnly = locked;
		return this;
	}

	isReadOnly(ctx: C) {
		if (typeof this._readOnly === 'function' && this._readOnly(ctx, this) === true) return true;
		if (this._readOnly === true) return true;
		return false;
	}

	/**
	 * Initialise les comportement standard d'un contrôle (visible, enabled, required, etc...).
	 * L'initialistion d'un custom doit se faire en surchargeant cette méthode pour bénéfier de l'affectation des attributs
	 */
	protected async _initStdControl(input: HTMLElement, ctx: C) {
		if (!this.isVisible(ctx)) input.setAttribute('hidden', '');
		if (!this.isEnabled(ctx)) input.setAttribute('disabled', '');
		if (this.isRequired(ctx)) input.setAttribute('required', '');
		if (this.isReadOnly(ctx)) input.setAttribute('readonly', '');

	}
}

/**
 * Area abstraite pour les contrôles acceptant un dataset
 */
export abstract class DatasetAsyncArea<E extends DatasetEntry<C>, C> extends ControlAsyncArea<C> {
	_dataset: Promise<Dataset<E>> | dynAny<C, Promise<Dataset<E>>>;

	setDataset(dataset: Promise<Dataset<E>> | dynAny<C, Promise<Dataset<E>>>): this {
		this._dataset = dataset;
		return this;
	}

	getDataset(ctx: C): Promise<Dataset<E>> {
		return (typeof this._dataset === 'function') ? this._dataset(ctx, this) : this._dataset;
	}
}

/**
 * Area construisant un élément <select> à partir d'un dataset asynchrone.
 */
export class SelectAsyncArea<E extends DatasetEntry<C>, C> extends DatasetAsyncArea<E, C> {
	_multiple: boolean | dynBoolean<C>;

	setMultiple(multiple: boolean | dynBoolean<C>): this {
		this._multiple = multiple;
		return this;
	}

	isMultiple(ctx: C) {
		if (typeof this._multiple === 'function' && this._multiple(ctx, this) === true) return true;
		if (this._multiple === true) return true;
		return false;
	}

	protected _buildControl(ctx: C, name: string, lastDatas?: JLastDatas): HTMLElement {
		const selectElt = <select name={name} class="loading" disabled="">
			<option key="" disabled="">Chargement...</option>
		</select> as HTMLSelectElement & IFormJsonisable & { jsonValue?: string | string[] };
		selectElt.multiple = this.isMultiple(ctx);
		if (!this.isVisible(ctx)) selectElt.setAttribute('hidden', '');
		const area = this;
		selectElt.extractJson = function (this: HTMLSelectElement & { jsonValue?: string | string[] }, parent: Dict<any>, forceClear: boolean): boolean {
			let value = parent[this.name];
			if (value === undefined && !forceClear) return false;
			if (this.classList.contains("loading")) {
				this.jsonValue = value; //Attente du chargement async du dataSet.
			} else {
				if (value == null) value = area.getDefaultValue(ctx);
				if (selectElt.multiple) {
					if (!Array.isArray(value)) value = value == null ? [] : [value]
					for (const option of selectElt.options) {
						option.selected = value.includes(option.value);
					}
				} else {
					selectElt.value = (Array.isArray(value) ? value[0] : value) || "";
				}
			}
			return true;
		}
		selectElt.fillJson = function (this: HTMLSelectElement & { jsonValue?: string | string[] }, parent: Jsonisable, root: Jsonisable) {
			if (this.classList.contains("loading")) {
				parent[this.name] = this.jsonValue;
			} else if (this.multiple) {
				const array = parent[this.name] = [] as string[];
				for (const o of this.selectedOptions) array.push((o as HTMLOptionElement).value)
			} else {
				parent[this.name] = this.value;
			}
		}
		this.initWidegt(ctx, selectElt);
		return selectElt;
	}

	async _loadControl(ctx: C, name: string, lastDatas?: JLastDatas): Promise<HTMLElement> {
		return this._buildControl(ctx, name, lastDatas);
	}

	async initWidegt(ctx: C, selectElt: HTMLSelectElement & { jsonValue?: string | string[] }) {
		const dataset = await this.getDataset(ctx);
		selectElt.required = this.isRequired(ctx)
		selectElt.disabled = this.isReadOnly(ctx)
		let defaultValue = selectElt.jsonValue !== undefined ? selectElt.jsonValue : this.getDefaultValue(ctx);
		if (defaultValue == null) defaultValue = [];
		else if (!Array.isArray(defaultValue)) defaultValue = [defaultValue];
		selectElt.textContent = null
		for (const key in dataset) {
			const data = dataset[key];
			const option = selectElt.appendChild(<option value={key}>{data.label}</option>) as HTMLOptionElement;
			option.selected = defaultValue.includes(key);
			option.disabled = !this.isEnabled(ctx) || (typeof data.disabled === 'function' ? data.disabled(ctx, this) : data.disabled);
			let desc = (typeof data.description === 'function' ? data.description(ctx, this) : data.description || "");
			if (desc) option.title = desc;
		}
		selectElt.classList.remove("loading");
	}
}


/**
 * Area regroupant les comportements pour les contrôle d'un formulaire (input, select et textarea)
 */
export abstract class ControlArea<C extends IControlAreaContext = IControlAreaContext> extends LabelArea<C> {
	protected _required: boolean | dynBoolean<C>;
	protected _defaultValue: any | dynAny<C>;
	protected _locked: any | dynBoolean<C>;

	setRequired(required: boolean | dynBoolean<C>): this {
		this._required = required;
		return this;
	}

	isRequired(ctx: C) {
		if (typeof this._required === 'function' && this._required(ctx, this) === true) return true;
		if (this._required === true) return true;
		return false;
	}

	/**
	 * Valeur par défaut de l'inputArea
	 * @type object
	 */
	setDefaultValue(defaultValue: any | dynAny<C>) {
		this._defaultValue = defaultValue;
		return this;
	}

	getDefaultValue(ctx: C) {
		return (typeof this._defaultValue === 'function') ? this._defaultValue(ctx, this) : this._defaultValue;
	}

	/**
	 * Indique que le champ est protégé, et enverra un évènement
	 * @param protected
	 */
	setReadOnly(locked: boolean | dynBoolean<C>): this {
		this._locked = locked;
		return this;
	}

	isReadOnly(ctx: C) {
		if (typeof this._locked === 'function' && this._locked(ctx, this) === true) return true;
		if (this._locked === true) return true;
		return false;
	}

	/** Initialise les comportement standard d'un contrôle (visible, enabled, required, etc...) */
	protected _initStdControl(input: HTMLElement, ctx: C) {
		if (!this.isVisible(ctx)) input.setAttribute('hidden', '');
		if (!this.isEnabled(ctx)) input.setAttribute('disabled', '');
		if (this.isRequired(ctx)) input.setAttribute('required', '');
		if (this.isReadOnly(ctx)) input.setAttribute('readonly', '');
	}
}

/**
 * Styles par défaut des forms basés sur ControlArea
 */
REG.reg.registerSkin('form-control-areas', 1, /* language=CSS */ `

	.ctrlLbl {
		display: flex;
		min-height: 0;
		min-width: 0;
		margin: 0.5em 0;
	}

	.ctrlLbl[hidden] {
		display: none;
	}

	.lbl {
		white-space: nowrap;
		min-width: 6em;
		margin-inline-end: 0.5em;
		color: var(--alt1-color);
		user-select: none;
	}

	.subPanel {
		margin-inline-start: 1.5em;
		padding-inline-start: 0.5em;
		border-inline-start: solid 2px var(--border-color);
	}

	label.choicevalue {
		white-space: nowrap;
		margin-inline-end: 0.5em;
		user-select: none;
	}

	.optionslist {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	.optionslist.vertical {
		display: grid;
		grid-template-columns: auto 1fr;
		grid-gap: 0rem .5rem;
		grid-auto-rows: max-content;
	}

	.optionslist.vertical label.choicevalue {
		white-space: unset;
		margin-inline-end: unset;
	}

	input, textarea, select {
		flex: 1;
		min-width: 0;
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		border: 1px solid var(--border-color);
		text-overflow: ellipsis;
		font-size: inherit;
	}

	input:disabled, textarea:disabled, select:disabled, .formElt:disabled {
		background-color: transparent;
	}

	input:focus-visible, textarea:focus-visible, select:focus-visible, .formElt:focus-visible {
		outline: var(--focus-outline);
	}

	input:invalid,
	textarea:invalid,
	select:invalid,
	.formElt:invalid {
		/* Pas outline car masque le focus (et le caret si input vide) */
		border-color: var(--error-color);
	}

	.formElt {
		/* Nécessaire pour matérialisation de l'état invalide */
		border: 1px solid transparent;
	}

	input[readonly], textarea[readonly], select[readonly] {
		background-color: transparent;
		border: none;
	}

	input[type=checkbox], input[type=radio] {
		flex: 0;
		min-width: initial;
	}

	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}

	input:focus::placeholder {
		color: transparent;
	}
`);

/**
 * Area construisant un élément <fieldset>
 */
export class SubControlsArea<C> extends ControlArea<C> {

	protected _areas: Area[] | dynAny<C, Area[]>;

	protected _hideLevel: Boolean | "uiOnly" | "formOnly";

	constructor(id?: string, areas?: Area[] | dynAny<C, Area[]>) {
		super(id);
		if (areas) this.setAreas(areas);
	}

	setAreas(areas: Area[] | dynAny<C, Area[]>): this {
		this._areas = areas;
		return this;
	}

	getAreas(ctx: C): Area[] | dynAny<C, Area[]> {
		return this._areas;
	}

	setHideLevel(level: Boolean | "uiOnly" | "formOnly"): this {
		this._hideLevel = level;
		return this;
	}

	getHideLevel(ctx: C): Boolean | "uiOnly" | "formOnly" {
		return this._hideLevel;
	}

	_buildControl(ctx: C, name?: string): HTMLFieldSetElement {
		let fieldsetElt = <fieldset name={(this._hideLevel === true || this._hideLevel === "formOnly") ? null : name}/> as HTMLFieldSetElement;
		this._initStdControl(fieldsetElt, ctx);
		let areas = this._areas;
		if (typeof areas == 'function') areas = areas(ctx, this);
		if (areas && areas.length) {
			AREAS.applyLayout(fieldsetElt, areas, ctx, true);
			fieldsetElt.hidden = false;
		} else
			fieldsetElt.hidden = true;
		return fieldsetElt;
	}

	protected _buildControlLabel(control: HTMLElement, ctx: C, label = this.getLabel(ctx)): HTMLElement {
		if (this._hideLevel === true || this._hideLevel === "uiOnly")
			return control;
		else
			return super._buildControlLabel(control, ctx, label);
	}
}

/**
 * Area construisant un élément <input>
 */
export class InputArea<C, IT extends string = string> extends ControlArea<C> {
	protected _inputType: IT | dynAny<C, IT>;
	protected _inputCustomValidity: (this: HTMLInputElement, ctx: C) => Promise<string | "">;
	protected _inputCustomValidityLatency: number = 0;
	protected _inputCustomValidityReportOnInput: boolean = false;
	private _onInputValiditySheduled: number = 0;

	constructor(id?: string, inputType?: IT) {
		super(id);
		if (inputType) this.setInputType(inputType);
	}

	getInputType(ctx: C): IT {
		return (typeof this._inputType === 'function') ? this._inputType(ctx, this) : this._inputType || "hidden" as IT;
	}

	setInputType(inputType: IT | dynAny<C, IT>): this {
		this._inputType = inputType;
		return this;
	}

	setInputCustomValidity(fct: (this: HTMLInputElement, ctx: C) => Promise<string | "">, delay?: number, reportOnInput?: boolean) {
		this._inputCustomValidity = fct;
		if (reportOnInput) this._inputCustomValidityReportOnInput = reportOnInput;
		if (delay) this._inputCustomValidityLatency = delay;
	}

	_buildControl(ctx: C, name: string): HTMLInputElement {
		const input = <input name={name} type={this.getInputType(ctx)}/> as HTMLInputElement;
		this._initStdControl(input, ctx);
		if (this._inputCustomValidity)
			input.addEventListener("input", async () => {
				if (this._onInputValiditySheduled) {
					clearTimeout(this._onInputValiditySheduled);
					this._onInputValiditySheduled = 0;
				}
				input.setCustomValidity("");
				this._onInputValiditySheduled = setTimeout(async () => {
					input.setCustomValidity(await this._inputCustomValidity.call(input, ctx));
					if (this._inputCustomValidityReportOnInput) input.reportValidity();
					input.dispatchEvent(new CustomEvent("x-validity-change", {bubbles: true, cancelable: false}));// permet le refresh de la validité du form
				}, this._inputCustomValidityLatency) as any;
			});
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) input.setAttribute('value', defaultValue);
		return input;
	}
}

type IStringInputType = "text" | "password" | "search" | "tel" | "url" | "email";

/**
 * Area représentant un élément <input> de type text, cad acceptant un placeholer et un pattern
 */
export class StringInputArea<C> extends InputArea<C, IStringInputType> {
	protected _placeholder: string | dynString<C>;
	protected _pattern: string | dynString<C>;

	constructor(id?: string, inputType: IStringInputType = "text") {
		super(id, inputType);
	}

	getPlaceholder(ctx: C) {
		return (typeof this._placeholder === 'function') ? this._placeholder(ctx, this) : this._placeholder;
	}

	setPlaceholder(placeholder: string | dynString<C>): this {
		this._placeholder = placeholder;
		return this;
	}

	setPattern(pattern: string | dynString<C>): this {
		this._pattern = pattern;
		return this;
	}

	getPattern(ctx: C) {
		return (typeof this._pattern === 'function') ? this._pattern(ctx, this) : this._pattern;
	}

	_buildControl(ctx: C, name: string): HTMLInputElement {
		const input = super._buildControl(ctx, name);
		const placeholder = this.getPlaceholder(ctx);
		if (placeholder) input.setAttribute('placeholder', placeholder);
		const pattern = this.getPattern(ctx);
		if (pattern) input.setAttribute('pattern', pattern);
		return input;
	}
}

/**
 * Area représentant un élément <input> de type hidden, cad non matérialisé, avec valeur fixe
 */
export class HiddenInputArea<C> extends InputArea<C, "hidden"> {
	protected _placeholder: string | dynString<C>;
	protected _pattern: string | dynString<C>;

	constructor(id?: string) {
		super(id, "hidden");
	}

	protected _buildControlLabel(control: HTMLElement, ctx: C, label = this.getLabel(ctx)): HTMLElement {
		// élément non matérialisé
		return control;
	}
}


/**
 * Par défaut, la valeur checked est true (avec une tolérance à "true")
 */
export class BoolCheckInputArea<C> extends InputArea<C> {
	constructor(id?: string) {
		super(id, "checkbox");
	}

	protected _checkedValue: string | dynString<C> | boolean | dynBoolean<C> = true;

	protected _uncheckedValue: string | dynString<C> | boolean | dynBoolean<C> = false;

	protected _isChecked: (val: any, ctx: C) => boolean = (val, ctx) => {
		const checkedValue = this.getCheckedValue(ctx);
		return checkedValue === true ? LANG.isTrue(val) : val == checkedValue
	}

	setIsChecked(fct: (val: any, ctx: C) => boolean): this {
		this._isChecked = fct;
		return this;
	}

	setCheckedValue(pattern: string | dynString<C> | boolean | dynBoolean<C>): this {
		this._checkedValue = pattern;
		return this;
	}

	getCheckedValue(ctx: C) {
		return (typeof this._checkedValue === 'function') ? this._checkedValue(ctx, this) : this._checkedValue;
	}

	setUncheckedValue(pattern: string | dynString<C> | boolean | dynBoolean<C>): this {
		this._uncheckedValue = pattern;
		return this;
	}

	getUncheckedValue(ctx: C) {
		return (typeof this._uncheckedValue === 'function') ? this._uncheckedValue(ctx, this) : this._uncheckedValue;
	}

	_buildControl(ctx: C, name: string): HTMLInputElement {
		const input = super._buildControl(ctx, name);

		const checkedValue = this.getCheckedValue(ctx);
		const uncheckedValue = this.getUncheckedValue(ctx);
		const isCheckedFct = this._isChecked;

		const checkedByDefault = Boolean(this.getDefaultValue(ctx));
		input.checked = checkedByDefault;

		Object.assign(input, {
			fillJson: function (this: HTMLInputElement, parent: Jsonisable, root: Jsonisable) {
				parent[this.name] = this.checked ? checkedValue : uncheckedValue;
			},
			extractJson: function (this: HTMLInputElement, parent: Dict<any>) {
				const value = parent[this.name];
				if (isCheckedFct(value, ctx))
					this.checked = true
				else if (value == null)
					this.checked = checkedByDefault
				else
					this.checked = false
				return true;
			}
		} as IFormJsonisable);
		return input;
	}
}

/** Date avec value lue et enregistrée en number EPOCH ou 0 si indéfini. */
export class DateInputArea<C> extends InputArea<C, "date" | "datetime-local"> {

	/**
	 * Nombre de jours de décalage entre la valeur enregistrée et celle affichée.
	 * Exemple : 1 permet d'afficher une date incluse
	 */
	deltaDays?: number;

	/**
	 * Si true : lit et enregistre la date au format string EPOCH (au lieu de number)
	 */
	asSring?: boolean;

	constructor(id?: string, deltaDays?: number, inputType: "date" | "datetime-local" = "date") {
		super(id, inputType);
		this.deltaDays = deltaDays || 0;
	}

	setAsString(asString: boolean): this {
		this.asSring = asString;
		return this;
	}

	_buildControl(ctx: C, name: string): HTMLInputElement {
		const input = super._buildControl(ctx, name);
		const area = this;
		Object.assign(input, {
			fillJson: function (this: HTMLInputElement, parent: Jsonisable, root: Jsonisable) {
				const d = this.valueAsNumber; //valueAsDate not impl avec type="datetime-local" (chrome 104)
				if (d) {
					const date = new Date(d);
					date.setMinutes(date.getMinutes() + date.getTimezoneOffset()); //On enregistre à 00:00 à la timeZone locale courante.
					if (area.deltaDays) date.setDate(date.getDate() + area.deltaDays);
					parent[this.name] = area.asSring ? date.getTime().toString() : date.getTime();
				} else {
					parent[this.name] = 0;
				}
			},
			extractJson: function (this: HTMLInputElement, parent: Dict<any>) {
				const value = area.asSring ? parseInt(parent[this.name]) : parent[this.name];
				if (value > 0) {
					const date = new Date(value);
					date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
					if (area.deltaDays) date.setDate(date.getDate() - area.deltaDays);
					date.setMilliseconds(0);//Pour pas d'affichage des ms dans l'input HTML
					this.valueAsNumber = date.getTime();
				} else this.value = null;
				return true;
			}
		} as IFormJsonisable);
		return input;
	}

}

/**
 * Area construisant un élément <textarea>
 */
export class InputTextArea<C> extends ControlArea<C> {
	protected _placeholder: string | dynString<C>;

	constructor(id?: string,) {
		super(id);
	}

	getPlaceholder(ctx: C) {
		return (typeof this._placeholder === 'function') ? this._placeholder(ctx, this) : this._placeholder;
	}

	setPlaceholder(placeholder: string | dynString<C>): this {
		this._placeholder = placeholder;
		return this;
	}

	_buildControl(ctx: C, name: string): HTMLTextAreaElement {
		const inputElt = <textarea name={name}/> as HTMLTextAreaElement;
		const placeholder = this.getPlaceholder(ctx);
		if (placeholder) inputElt.setAttribute('placeholder', placeholder);
		this._initStdControl(inputElt, ctx);
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) inputElt.setAttribute('value', defaultValue);
		return inputElt;
	}
}

/**
 * Area construisant un élément <textarea> d'édition d'un JSON
 */
export class InputJsonArea<C> extends InputTextArea<C> {

	_buildControl(ctx: C, name: string): HTMLTextAreaElement {
		const control = super._buildControl(ctx, name);
		Object.assign(control, {
			fillJson: function (this: HTMLTextAreaElement, parent: Jsonisable, root: Jsonisable) {
				parent[this.name] = this.value ? JSON.parse(this.value) : null;
			},
			extractJson: function (this: HTMLTextAreaElement, parent: Dict<any>) {
				const value = parent[this.name];
				if (value == null) this.value = "";
				else if (typeof value == "string") this.value = value;
				else this.value = JSON.stringify(value);
				return true;
			}
		} as IFormJsonisable);
		return control;
	}
}

export interface DatasetEntry<C> {
	label: string | dynString<C>,
	icon?: string | dynString<C>,
	disabled?: boolean | dynBoolean<C>,
	description?: string | dynString<C>,
}

export interface DatasetEntryMultiLevels<C> extends DatasetEntry<C> {
	/** si true, le groupe est sélectionnable */
	isListEntry?: boolean | dynBoolean<C>,
	subs?: Dict<DatasetEntryMultiLevels<C>>,
}

export type Dataset<E extends DatasetEntry<C> = any, C = any> = Dict<E>;

export type DatasetMultiLevels<E extends DatasetEntryMultiLevels<C> = any, C = any> = Dict<E>;

/**
 * Area abstraite pour les contrôles acceptant un dataset
 */
export abstract class DatasetArea<E extends DatasetEntry<C>, C> extends ControlArea<C> {
	_dataset: Dataset<E> | dynAny<C, Dataset<E>>;

	setDataset(dataset: Dataset<E> | dynAny<C, Dataset<E>>): this {
		this._dataset = dataset;
		return this;
	}

	getDataset(ctx: C): Dataset<E> {
		return (typeof this._dataset === 'function') ? this._dataset(ctx, this) : this._dataset;
	}
}

/**
 * Area construisant une liste d'éléments checkbox ou radio partir d'un dataset
 */
export class InputChoiceArea<C> extends DatasetArea<DatasetEntry<C>, C> {
	protected _inputType: "checkbox" | "radio" | dynAny<C, "checkbox" | "radio"> = "radio";

	protected _direction: "vertical" | "horizontal" = "horizontal";

	protected jsonBinderMode?: 'boolean' | 'array' | null;

	constructor(id?: string, inputType?: "checkbox" | "radio") {
		super(id);
		if (inputType) this.setInputType(inputType);
	}

	getInputType(ctx: C): "checkbox" | "radio" {
		return (typeof this._inputType === 'function') ? this._inputType(ctx, this) : this._inputType || "radio" as "checkbox" | "radio";
	}

	setInputType(inputType: "checkbox" | "radio" | dynAny<C, "checkbox" | "radio">): this {
		this._inputType = inputType;
		return this;
	}

	setDirection(value: "vertical" | "horizontal") {
		this._direction = value;
	}

	setJsonBinderMode(mode: 'boolean' | 'array' | null /*todo | 'booleanNegate'  | 'arrayNegate' */): this {
		this.jsonBinderMode = mode;
		return this;
	}

	_buildControl(ctx: C, name: string): HTMLDivElement {
		//data-direction=vertical
		let fieldset: HTMLDivElement = <div class={`optionslist ${this._direction}`}/> as HTMLDivElement;
		this._initStdControl(fieldset, ctx);
		const dataset = this.getDataset(ctx);
		let defaultValue = this.getDefaultValue(ctx);
		if (!Array.isArray(defaultValue)) {
			if (defaultValue == null) defaultValue = [];
			else defaultValue = [defaultValue];
		}
		const isGlobalDisabled = fieldset.hasAttribute("disabled"); //calculé dans _initStdControl()
		const fieldType = this.getInputType(ctx);
		for (const key in dataset) {
			const data = dataset[key];
			const id = name + "_" + key;
			const input = fieldset.appendChild(<input name={name} type={fieldType} value={key} id={id}/>) as HTMLInputElement;
			const label = fieldset.appendChild(<label class="choicevalue" for={id}>{data.label}</label>);
			input.checked = defaultValue.includes(key);
			input.disabled = isGlobalDisabled || (typeof data.disabled === 'function' ? data.disabled(ctx, this) : data.disabled);
			let desc = (typeof data.description === 'function' ? data.description(ctx, this) : data.description || "");
			if (desc) label.title = input.title = desc;
		}
		this.initJsonBinders(fieldset, name, this.jsonBinderMode);
		return fieldset;
	}

	/** À surcharger si les modes de binders par défaut ne conviennent pas. */
	initJsonBinders(fieldSet: HTMLDivElement, name: string, mode: 'boolean' | 'array' /*todo | 'booleanNegate'  | 'arrayNegate' */) {
		if (!mode) return;
		if (mode === 'boolean') {
			Object.assign(fieldSet, {
				fillJson: function (this: HTMLDivElement, parent: Jsonisable, root: Jsonisable) {
					let input = this.querySelector("input:checked") as HTMLInputElement;
					if (input) parent[name] = input.value === "true"; //input.value est la key static ici !== input.checked
				},
				extractJson: function (this: HTMLDivElement, parent: Dict<any>) {
					//input.value est la key static ici !== input.checked
					if (parent[name]) {
						(this.querySelector("input[value='true']") as HTMLInputElement).checked = true;
					} else {
						(this.querySelector("input[value='false']") as HTMLInputElement).checked = true;
					}
					return true;
				}
			} as IFormJsonisable);
		} else if (mode === 'array') {
			Object.assign(fieldSet, {
				fillJson: function (this: HTMLDivElement, parent: Jsonisable, root: Jsonisable) {
					const r: string[] = [];
					DOM.findNext(this, this, (n: Node): n is Node => {
						if (n instanceof HTMLInputElement && n.checked) r.push(n.value);
						return false;
					});
					parent[name] = r;
				},
				extractJson: function (this: HTMLDivElement, parent: Dict<any>) {
					const array = parent[name];
					if (Array.isArray(array)) {
						DOM.findNext(this, this, (n: Node): n is Node => {
							if (n instanceof HTMLInputElement) n.checked = array.indexOf(n.value) >= 0;
							return false;
						});
						return true;
					}
					return false;
				}
			} as IFormJsonisable);
		}
	}
}


/**
 * Area construisant un élément <select> à partir d'un dataset
 */
export class SelectArea<E extends DatasetEntryMultiLevels<C>, C> extends DatasetArea<E, C> {
	_multiple: boolean | dynBoolean<C>;

	setMultiple(multiple: boolean | dynBoolean<C>): this {
		this._multiple = multiple;
		return this;
	}

	isMultiple(ctx: C) {
		if (typeof this._multiple === 'function' && this._multiple(ctx, this) === true) return true;
		if (this._multiple === true) return true;
		return false;
	}

	_buildControl(ctx: C, name: string): HTMLSelectElement {
		const selectElt = <select name={name}/> as HTMLSelectElement;
		this._initStdControl(selectElt, ctx);
		// Pas de select.readonly => disabled dans ce cas
		if (this.isReadOnly(ctx)) selectElt.setAttribute('disabled', '');
		selectElt.multiple = this.isMultiple(ctx);
		const dataset = this.getDataset(ctx);
		let defaultValue = this.getDefaultValue(ctx);
		if (!Array.isArray(defaultValue)) {
			if (defaultValue == null) defaultValue = [];
			else defaultValue = [defaultValue];
		}
		const doLevel = (currentElt: HTMLElement, objLevel: Dict<DatasetEntryMultiLevels<C>>) => {
			for (const key in objLevel) {
				const data = objLevel[key];
				if (data.subs) {
					const optiongroup = currentElt.appendChild(<optgroup label={data.label}/>) as HTMLOptGroupElement;
					let desc = (typeof data.description === 'function' ? data.description(ctx, this) : data.description || "");
					if (desc) optiongroup.title = desc;
					if (data.isListEntry) {
						const option = optiongroup.appendChild(<option value={key}>{data.label}</option>) as HTMLOptionElement;
						option.selected = defaultValue.includes(key);
						option.disabled = !this.isEnabled(ctx) || (typeof data.disabled === 'function' ? data.disabled(ctx, this) : data.disabled);
						let desc = (typeof data.description === 'function' ? data.description(ctx, this) : data.description || "");
						if (desc) option.title = desc;

					}
					doLevel(optiongroup, data.subs);
				} else {
					const option = currentElt.appendChild(<option value={key}>{data.label}</option>) as HTMLOptionElement;
					option.selected = defaultValue.includes(key);
					option.disabled = !this.isEnabled(ctx) || (typeof data.disabled === 'function' ? data.disabled(ctx, this) : data.disabled);
					let desc = (typeof data.description === 'function' ? data.description(ctx, this) : data.description || "");
					if (desc) option.title = desc;
				}
			}
		};
		doLevel(selectElt, dataset);
		return selectElt;
	}

}

export interface DatasetEntrySubAreas<C> extends DatasetEntry<C> {
	areas?: Area[] | dynAny<C, Area[]>
}

/**
 * Area construisant un élément <select> à partir d'un dataset ainsi qu'un sous-panneaux à partir
 * de la liste d'areas précisées pour cette clé dans le dataset
 */
export class SelectSubPanelArea<E extends DatasetEntrySubAreas<C>, C extends IControlAreaContext> extends SelectArea<E, C> {
	isMultiple(ctx: C) {
		return false;
	}

	buildBody(ctx: C): HTMLElement {
		const area = this;
		const body = <div/>;
		const control = this._buildControl(ctx, this.getId());
		body.appendChild(ctx.buildControlLabel ? this._buildControlLabel(control, ctx) : control);

		const subPanel = body.appendChild(<div class="subPanel" hidden/>) as HTMLDivElement;
		control.addEventListener('change', async (ev: UIEvent) => {
			const select = ev.target as HTMLSelectElement;
			this._selectSubPanel(ctx, subPanel, select.value)
		});
		Object.assign(control, {
			fillJson: function (this: HTMLSelectElement, parent: Jsonisable, root: Jsonisable) {
				parent[this.name] = this.value;
			},
			extractJson: function (this: HTMLSelectElement, parent: Dict<any>) {
				this.value = parent[this.name];
				area._selectSubPanel(ctx, subPanel, this.value);
				return true;
			}
		} as IFormJsonisable);
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) this._selectSubPanel(ctx, subPanel, defaultValue);
		body.setAttribute('area-id', this.getId());
		return body;
	}

	async loadBody(ctx: C): Promise<HTMLElement> {
		const area = this;
		const ctn = <div/>;
		const control = this._buildControl(ctx, this.getId());
		ctn.appendChild(ctx.buildControlLabel ? this._buildControlLabel(control, ctx) : control);

		const subPanel = ctn.appendChild(<div class="subPanel" hidden/>) as HTMLDivElement;
		control.addEventListener('change', async (ev: UIEvent) => {
			const select = ev.target as HTMLSelectElement;
			await this._selectSubPanelAsync(ctx, subPanel, select.value)
		});
		Object.assign(control, {
			fillJson: function (this: HTMLSelectElement, parent: Jsonisable, root: Jsonisable) {
				parent[this.name] = this.value;
			},
			extractJson: function (this: HTMLSelectElement, parent: Dict<any>) {
				this.value = parent[this.name];
				area._selectSubPanel(ctx, subPanel, this.value);
				return true;
			}
		} as IFormJsonisable);
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) await this._selectSubPanelAsync(ctx, subPanel, defaultValue);

		return ctn;
	}

	_selectSubPanel(ctx: C, subPanel: HTMLDivElement, key: string) {
		const dataset = this.getDataset(ctx);
		let areas = key in dataset && dataset[key].areas;
		if (typeof areas == 'function') areas = areas(ctx, this);
		subPanel.innerHTML = '';
		if (areas && areas.length) {
			AREAS.applyLayout(subPanel, areas, ctx, true);
			subPanel.hidden = false;
		} else {
			subPanel.hidden = true;
		}
	}

	async _selectSubPanelAsync(ctx: C, subPanel: HTMLDivElement, key: string) {
		const dataset = this.getDataset(ctx);
		let areas = key in dataset && dataset[key].areas;
		if (typeof areas == 'function') areas = areas(ctx, this);
		if (areas && areas.length) {
			await AREAS.applyLayout(subPanel, areas, ctx, true);
			subPanel.hidden = false;
		} else {
			subPanel.innerHTML = '';
			subPanel.hidden = true;
		}
	}

	needAsync(ctx: C): boolean {
		const dataset = this.getDataset(ctx);
		for (const key in dataset) {
			let areas = key in dataset && dataset[key].areas;
			if (typeof areas == 'function') areas = areas(ctx, this);
			if (areas) for (const area of areas) {
				if (area.needAsync(ctx)) return true;
			}
		}
		return false;
	}
}

/**
 * Area de construction d'un bouton à partir d'un svcAction
 */
export class ActionBtnArea<C extends IRegPointer<any>> extends Area<C> {
	protected _svcAction: string | dynString<C> | IAction<C>;

	protected _uiContext: EButtonUiContext | dynString<C>;

	constructor(id?: string, svcAction?: string | dynString<C> | IAction<C>) {
		super(id);
		if (svcAction) this.setSvcAction(svcAction);
	}

	setSvcAction(svcActionCd: string | dynString<C> | IAction<C>): this {
		this._svcAction = svcActionCd;
		return this;
	}

	setUiContext(uictx: EButtonUiContext | dynString<C>): this {
		this._uiContext = uictx;
		return this;
	}

	getSvcAction(ctx: C): IAction<C> | null {
		let action = (typeof this._svcAction === 'function') ? this._svcAction(ctx, this) : this._svcAction;
		if (action && typeof action === 'string')
			return ctx.reg.getSvc<IAction<C>>(action);
		return action as IAction<C>;
	}

	getUiContext(ctx: C) {
		return (typeof this._uiContext === 'function') ? this._uiContext(ctx, this) : this._uiContext;
	}

	buildBody(ctx: C): HTMLElement {
		return ActionBtn.buildButton(this.getSvcAction(ctx), ctx, this.getUiContext(ctx));
	}
}

export namespace AREAS {

	export const NULL = Object.freeze(new Area('#NULL').setVisible(false)) as Area<any, any>;

	/**
	 * Crée et réparti les areas en fonction de règle de sélection par id ou group.
	 * Si utilisé sur des slots dans un shadowRoot, les éléments sont ajoutés sur le host et répartis par la propriété slot
	 *
	 * @example
	 * <div id="div">
	 *   <div area-ids="area1 area2"></div>
	 *   <div area-groups="areaGroup" />
	 *   <div area-ids="*" />
	 * </div>

	 *
	 * @param root
	 * @param {Area<C,E>[]} areasList Liste ou map des areas à répartir
	 * @param {C} ctx Contexte pour construire les areas.
	 * @param {boolean} clear Dans le cas d'un deuxième appel, permet de supprimer les précédentes areas
	 * @param raceCond Fonction optionnelle qui doit retrourner true si une raceCond est détectée après une traiement asynchrone et permet d'annuler le traitement et d'interrompre la modification de root.
	 * @returns {Promise<Element[] | null>} Promesse retournant les corps des areas construites ou null si une raceCond a été détectée.
	 */
	export async function applyLayout<C>(root: Element | ShadowRoot | DocumentFragment, areasList: IArea<C>[] | Dict<IArea<C>>, ctx: C, clear = false, raceCond?: () => boolean): Promise<Dict<Element>> {
		const doc = root.ownerDocument;
		const areasSet = new Set<IArea<C>>(Array.isArray(areasList) ? areasList : Object.values(areasList));
		const bodies: Dict<Element> = {};
		const idsParents = [], groupsParents = [];
		let remainParent = null;
		const it = doc.createNodeIterator(root, NodeFilter.SHOW_ELEMENT);
		let elt;
		while (elt = it.nextNode() as Element) {
			const ids = elt.getAttribute('area-ids');
			if (ids) {
				if (ids != '*') {
					idsParents.push(elt);
					if (elt instanceof HTMLSlotElement && !elt.name) elt.name = ids;
				} else if (!remainParent) {
					remainParent = elt;
				}

				if (clear) {
					if (elt instanceof HTMLSlotElement) {
						const assignedNodes = elt.assignedNodes();
						assignedNodes.forEach((node) => node.parentNode.removeChild(node));
					} else {
						while (elt.firstChild) elt.removeChild(elt.lastChild);
					}
				}
			}
			const groups = elt.getAttribute('area-groups');
			if (groups) {
				groupsParents.push(elt);
				if (elt instanceof HTMLSlotElement && !elt.name) elt.name = groups;
				if (clear) {
					if (elt instanceof HTMLSlotElement) {
						const assignedNodes = elt.assignedNodes();
						assignedNodes.forEach((node) => node.parentNode.removeChild(node));
					} else {
						while (elt.firstChild) elt.removeChild(elt.lastChild);
					}
				}
			}
		}
		if (!remainParent) {
			remainParent = root;
			if (clear) while (remainParent.lastChild) remainParent.lastChild.remove();
		}

		for (let i = 0; i < idsParents.length; i++) {
			const parent = idsParents[i];
			const ids = parent.getAttribute('area-ids').split(' ');
			for (const area of areasSet) {
				if (ids.includes(area.getId())) {
					const needAsync = area.needAsync(ctx);
					const body = needAsync ? await area.loadBody(ctx) : area.buildBody(ctx);
					if (needAsync && raceCond && raceCond()) return null;
					if (body) {
						if (parent instanceof HTMLSlotElement && body instanceof HTMLElement && root instanceof ShadowRoot) {
							body.slot = (parent as HTMLSlotElement).name;
							root.host.appendChild(body);
						} else {
							parent.appendChild(body);
						}

						bodies[area.getId()] = body;
					}
					areasSet.delete(area);
				}
			}
		}

		if (areasSet.size) {
			for (let i = 0; i < groupsParents.length; i++) {
				const parent = groupsParents[i];
				const groups = parent.getAttribute('area-groups').split(' ');
				for (const area of areasSet) {
					const areaGroup = area.getGroup(ctx);
					if (areaGroup && groups.includes(areaGroup)) {
						const needAsync = area.needAsync(ctx);
						const body = needAsync ? await area.loadBody(ctx) : area.buildBody(ctx);
						if (needAsync && raceCond && raceCond()) return null;
						if (body) {
							if (parent.localName == 'slot' && body instanceof HTMLElement && root instanceof ShadowRoot) {
								body.slot = (parent as HTMLSlotElement).name;
								root.host.appendChild(body);
							} else {
								parent.appendChild(body);
							}
							bodies[area.getId()] = body;
						}
						areasSet.delete(area);
					}
				}
			}
		}

		if (areasSet.size) {
			for (const area of areasSet) {
				const needAsync = area.needAsync(ctx);
				const body = needAsync ? await area.loadBody(ctx) : area.buildBody(ctx);
				if (needAsync && raceCond && raceCond()) return null;
				if (body) {
					if (DOM.IS_element(remainParent) && remainParent.localName == 'slot' && body instanceof HTMLElement && root instanceof ShadowRoot) {
						root.host.appendChild(body);
					} else {
						remainParent.appendChild(body);
					}
					bodies[area.getId()] = body;
				}
			}
		}

		return bodies;
	}

	/**
	 * Construction standard d'un label autour d'un Input.
	 */
	export function buildControlLabel(control: HTMLElement, id: string, label: string): HTMLElement {
		control.id = id;
		return <div class="ctrlLbl">
			<label class="lbl" for={id}>{label}</label>
			{control}
		</div>;
	}
}
